import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  const backgroundImageUrl = `${process.env.PUBLIC_URL}/images/login-bg.png`;

  const verifyToken = useCallback(async () => {
    try {
      const result = await authService.verifyResetToken(token!);
      setTokenValid(result.valid);
    } catch {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token, verifyToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token!, password);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const inputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = '#1a73e8';
    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(26,115,232,0.15)';
  };
  const inputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = '#dadce0';
    e.currentTarget.style.boxShadow = 'none';
  };

  const renderContent = () => {
    if (verifying) {
      return <p style={styles.heading}>Verifying reset link...</p>;
    }

    if (!tokenValid) {
      return (
        <>
          <div style={styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <p style={styles.heading}>Invalid or expired link</p>
          <p style={styles.subHeading}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <button style={styles.continueBtn}>Request New Link</button>
          </Link>
        </>
      );
    }

    if (success) {
      return (
        <>
          <div style={styles.successIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <p style={styles.heading}>Password reset successful!</p>
          <p style={styles.subHeading}>
            Your password has been changed. You can now log in with your new password.
          </p>
          <button style={styles.continueBtn} onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </>
      );
    }

    return (
      <>
        <p style={styles.heading}>Set your new password</p>
        <p style={styles.subHeading}>
          Enter a new password for your account. Must be at least 6 characters.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password*"
                required
                minLength={6}
                style={{ ...styles.input, paddingRight: '48px' }}
                onFocus={inputFocus}
                onBlur={inputBlur}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password*"
              required
              minLength={6}
              style={styles.input}
              onFocus={inputFocus}
              onBlur={inputBlur}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ ...styles.continueBtn, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </>
    );
  };

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.bgImage, backgroundImage: `url(${backgroundImageUrl})` }} />
      <div style={styles.overlay} />

      <div style={styles.cardOuter}>
        <div style={styles.card}>
          {/* WTI Logo */}
          <div style={styles.logoSection}>
            <div style={styles.brandRow}>
              <span style={styles.brandLetter}>w</span>
              <span style={styles.brandLetter}>t</span>
              <span style={styles.brandLetterI}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/logo.png`}
                  alt="WTI"
                  style={styles.logoPin}
                />
                <span style={styles.dotlessI}>ı</span>
              </span>
            </div>
            <div style={styles.brandSubtext}>where to invest</div>
          </div>

          {renderContent()}

          <p style={styles.bottomRow}>
            <Link to="/login" style={styles.link}>Back to login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    minHeight: '100vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bgImage: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1,
  },
  cardOuter: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '440px',
    padding: '24px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #1a73e8',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    padding: '48px 40px 36px',
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: '24px',
  },
  brandRow: {
    display: 'inline-flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    lineHeight: 1,
  },
  brandLetter: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  brandLetterI: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    position: 'relative' as const,
    lineHeight: 1,
    display: 'inline-block',
  },
  dotlessI: {
    fontSize: '48px',
    fontWeight: 900,
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  logoPin: {
    position: 'absolute' as const,
    top: '-6px',
    left: '50%',
    transform: 'translateX(-50%)',
    height: '28px',
    width: 'auto',
  },
  brandSubtext: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1a73e8',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  heading: {
    textAlign: 'center' as const,
    fontSize: '16px',
    color: '#3c4043',
    fontWeight: 400,
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  subHeading: {
    textAlign: 'center' as const,
    fontSize: '14px',
    color: '#5f6368',
    marginBottom: '28px',
    lineHeight: '1.6',
  },
  successIcon: {
    textAlign: 'center' as const,
    marginBottom: '16px',
  },
  errorIcon: {
    textAlign: 'center' as const,
    marginBottom: '16px',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    color: '#202124',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box' as const,
  },
  passwordWrapper: {
    position: 'relative' as const,
  },
  eyeButton: {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueBtn: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#1a73e8',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    letterSpacing: '0.3px',
  },
  bottomRow: {
    marginTop: '24px',
    textAlign: 'center' as const,
    fontSize: '14px',
    color: '#5f6368',
  },
  link: {
    color: '#1a73e8',
    fontWeight: 600,
    textDecoration: 'none',
  },
};

export default ResetPassword;
